import { supabase } from '@/integrations/supabase/client';
import { BaseService } from './BaseService';

export interface UserActivity {
  id: string;
  user_id: string;
  activity_type: string;
  activity_data: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

// Module-level throttles. Reset on full page reload (desired window boundary).
// trackLogin: avoid both the dedup SELECT and the profile UPDATE inside the
// 5-minute window — the SIGNED_IN event re-fires on token refresh / tab focus.
const RECENT_LOGIN_TRACK = new Map<string, number>();
const LOGIN_DEDUP_WINDOW_MS = 5 * 60 * 1000;

// trackLastActive: SPA navigation triggers this on every route change.
const RECENT_ACTIVE_TRACK = new Map<string, number>();
const ACTIVE_DEDUP_WINDOW_MS = 5 * 60 * 1000;

class UserActivityService extends BaseService {
  constructor() {
    super('UserActivityService');
  }

  async logActivity(
    activityType: string,
    activityData: Record<string, any> = {}
  ): Promise<void> {
    return this.execute(
      async () => {
        const user = await this.getCurrentUser();

        await supabase.from('user_activity_log').insert({
          user_id: user.id,
          activity_type: activityType,
          activity_data: activityData,
          user_agent: navigator.userAgent
        });
      },
      { action: 'logActivity', metadata: { activityType } }
    );
  }

  async getUserActivity(userId: string, limit: number = 50): Promise<UserActivity[]> {
    return this.execute(
      async () => {
        const { data, error } = await supabase
          .from('user_activity_log')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(limit);

        if (error) throw error;
        return (data || []) as UserActivity[];
      },
      { action: 'getUserActivity', metadata: { userId, limit } }
    );
  }

  async trackLogin(userId: string): Promise<void> {
    return this.execute(
      async () => {
        // Cheap in-memory throttle first — skips the dedup SELECT entirely
        // when SIGNED_IN re-fires within the window for the same tab.
        const lastTrackedAt = RECENT_LOGIN_TRACK.get(userId);
        if (lastTrackedAt && Date.now() - lastTrackedAt < LOGIN_DEDUP_WINDOW_MS) {
          return;
        }

        // DB-side dedup for fresh tabs: another tab may have logged recently.
        const { data: lastLogin } = await supabase
          .from('user_activity_log')
          .select('created_at')
          .eq('user_id', userId)
          .eq('activity_type', 'login')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (lastLogin) {
          const lastLoginTime = new Date(lastLogin.created_at).getTime();
          if (Date.now() - lastLoginTime < LOGIN_DEDUP_WINDOW_MS) {
            // Already tracked recently from another tab — skip BOTH the
            // activity row AND the redundant profile UPDATE.
            RECENT_LOGIN_TRACK.set(userId, Date.now());
            return;
          }
        }

        // Update last login in profiles
        await supabase
          .from('profiles')
          .update({ 
            last_login_at: new Date().toISOString(),
            failed_login_attempts: 0 
          })
          .eq('user_id', userId);

        // Log activity
        await this.logActivity('login', { timestamp: new Date().toISOString() });
        RECENT_LOGIN_TRACK.set(userId, Date.now());
      },
      { action: 'trackLogin', metadata: { userId } }
    );
  }

  async trackLastActive(): Promise<void> {
    return this.execute(
      async () => {
        const user = await this.getCurrentUser();
        const lastAt = RECENT_ACTIVE_TRACK.get(user.id);
        if (lastAt && Date.now() - lastAt < ACTIVE_DEDUP_WINDOW_MS) {
          return;
        }
        RECENT_ACTIVE_TRACK.set(user.id, Date.now());
        await supabase
          .from('profiles')
          .update({ last_active_at: new Date().toISOString() })
          .eq('user_id', user.id);
      },
      { action: 'trackLastActive' }
    );
  }
}

export const userActivityService = new UserActivityService();
